import { useState, useEffect } from 'react';
import { migrateLocalStorageToIndexedDB } from '../utils/catalogMigration';

interface AppInitializerProps {
  children: React.ReactNode;
}

/**
 * Root-level initialization component that runs migrations before rendering the app.
 * Handles localStorage → IndexedDB migration for the Character Catalog feature.
 */
export function AppInitializer({ children }: AppInitializerProps) {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      try {
        const result = await migrateLocalStorageToIndexedDB();

        // Log migration results for debugging
        if (result.migrated) {
          console.log(
            `Migration complete: ${result.characterName || 'character'} migrated to catalog`
          );
        }

        if (result.warnings.length > 0) {
          console.warn('Migration warnings:', result.warnings);
        }

        if (!result.success && result.errors.length > 0) {
          console.error('Migration errors:', result.errors);
        }
      } catch (error) {
        // Don't block app startup on migration failure
        console.error('Unexpected migration error:', error);
      }

      setIsReady(true);
    };

    initialize();
  }, []);

  if (!isReady) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-slate-900">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-amber-500 border-t-transparent" />
          <span className="text-sm text-slate-400">Loading...</span>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
